import React from 'react';
import { util } from 'veritone-redux-common';
import { connect } from 'react-redux';
import { some, pick, map, isEmpty } from 'lodash';
import { string, shape, func, bool, arrayOf, object } from 'prop-types';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import Edit from '@material-ui/icons/Edit';
import Grid from '@material-ui/core/Grid';

import * as apps from 'modules/application';
import { PropTypes, injectInto } from 'helpers/react';

import withUIState from 'shared-components/withUIState';
import withData from 'shared-components/withData';
import { Container } from 'shared-components/grid';
import FailureScreen from 'shared-components/FailureScreen';
import OverviewNullState from 'shared-components/OverviewNullState';
import MenuWrapper from 'shared-components/MenuWrapper';
import DocumentationLink from 'shared-components/DocumentationLink';

import { requireFields, composeValidations } from 'helpers/redux/form';

import appPackageComponentModel from 'modules/application/app-package-model';
import { appPackageComponentTypes } from 'modules/application/constants';
import noComponentsImg from 'resources/images/no-components.png';
import AppPackageHeader from './AppPackageHeader';
import AppPackageComponentForm from './AppPackageComponentForm';

import rootSaga from '../modules/details-sagas';
import styles from '../styles/applicationDetailsContainer.scss';

const saga = util.reactReduxSaga.saga;
const { routerShape, children } = PropTypes;

@saga(rootSaga)
@withUIState({
  key: 'application-details',
  defaultState: props => ({
    // put current id in state so sagas can reference it
    id: props.params.id
  })
})
@withData([
  {
    action: ownProps => apps.fetchApplication(ownProps.params.id),
    blockPageLoad: true
  }
])
@connect(
  (state, ownProps) => ({
    application: apps.getApplications(state)[ownProps.params.id],
    oauthSecretLoading:
      apps.getLoadingOAuthSecretApplication(state) === ownProps.params.id,
    oauthSecretLoadingFailureMessage: apps.getFailedLoadingOAuthSecretMessage(
      state
    ),
    oauthSecretLoadingFailed:
      apps.getFailedLoadingOAuthSecretApplication(state) === ownProps.params.id,
    componentListByGroup: apps.getAppPackageComponentsList(
      state,
      ownProps.params.id
    )
  }),
  pick(apps, [
    'fetchApplicationOauthSecret',
    'updateAppPackageComponents',
    'updateApplicationField',
    'createContextMenuExtension',
    'updateContextMenuExtension',
    'deleteApplication',
    'doApplicationAction'
  ])
)
export default class ApplicationDetailsContainer extends React.Component {
  static propTypes = {
    params: shape({ id: string }).isRequired,
    application: shape({
      id: string,
      description: string,
      status: string,
      iconUrl: string,
      validStateActions: arrayOf(string)
    }).isRequired,
    error: string,
    children,
    router: routerShape,
    fetchApplicationOauthSecret: func,
    oauthSecretLoading: bool,
    oauthSecretLoadingFailed: bool,
    oauthSecretLoadingFailureMessage: string,
    componentListByGroup: shape({
      engines: arrayOf(object),
      dataRegistries: arrayOf(object),
      contextMenuExtensions: arrayOf(object),
      oauth2RedirectUrls: arrayOf(object),
      url: arrayOf(object)
    }),
    updateAppPackageComponents: func,
    updateApplicationField: func,
    createContextMenuExtension: func,
    updateContextMenuExtension: func,
    deleteApplication: func,
    doApplicationAction: func
  };

  static defaultProps = {
    application: {}
  };

  state = {
    showComponentMenu: false,
    selectedComponentType: '',
    initialValues: {},
    isEditing: false
  };

  handlePageChange = value => {
    this.props.router.push(
      `/applications/${this.props.router.params.id}/${value}`
    );
  };

  handleRevealOauthSecret = password => {
    this.props.fetchApplicationOauthSecret(this.props.application.id, password);
  };

  openNewComponentForm = componentType => {
    this.setState({
      selectedComponentType: componentType,
      initialValues: {},
      isEditing: false
    });
  };

  closeNewComponentForm = () => {
    this.setState({
      selectedComponentType: ''
    });
  };

  handleSubmitComponentData = (componentType, payload) => {
    const { application: { id: applicationId } } = this.props;
    const values = payload[componentType];

    switch (componentType) {
      case 'engine':
      case 'dataSchema':
        this.props.updateAppPackageComponents(applicationId, componentType, {
          ids: [values],
          action: 'add'
        });
        break;
      case 'contextMenuExtension':
        this.state.isEditing
          ? this.props.updateContextMenuExtension(applicationId, values)
          : this.props.createContextMenuExtension(applicationId, values);
        break;
      case 'oauth2RedirectUrl':
        this.props.updateApplicationField(applicationId, 'oauth2RedirectUrls', [
          values
        ]);
        break;
      case 'appSwitcherShortcut':
        this.props.updateApplicationField(applicationId, 'url', values);
        break;
      default:
        break;
    }

    this.closeNewComponentForm();
  };

  handleComponentCardClick = (component, componentType) => {
    switch (componentType) {
      case 'engine':
        this.props.router.push(`/engines/${component.id}`);
        break;
      case 'dataSchema':
        this.props.router.push(`/data-schemas/${component.id}`);
        break;
      case 'oauth2RedirectUrl':
      case 'appSwitcherShortcut':
        this.setState({
          selectedComponentType: componentType,
          initialValues: {
            [componentType]: component.url
          },
          isEditing: true
        });
        break;
      case 'contextMenuExtension':
        this.setState({
          selectedComponentType: componentType,
          initialValues: {
            [componentType]: component
          },
          isEditing: true
        });
        break;
      default:
        break;
    }
  };

  handleApplicationAction = action => {
    const { application } = this.props;

    if (action === 'delete') {
      return this.props.deleteApplication(application);
    }

    if (action === 'edit') {
      return this.props.router.push(
        `/applications/${application.id}/configuration/edit`
      );
    }

    if (!apps.validActions.includes(action)) {
      return;
    }

    this.props.doApplicationAction(action, application);
  };

  handleEditApplication = () => {
    const { application } = this.props;

    this.props.router.push(
      `/applications/${application.id}/configuration/edit`
    );
  };

  render() {
    const { application, componentListByGroup } = this.props;
    const { selectedComponentType } = this.state;
    const { label: selectedComponentLabel } =
      appPackageComponentTypes.find(
        component => component.type === selectedComponentType
      ) || {};
    const hasComponents = some(componentListByGroup, 'length');

    return (
      <div>
        <Container
          topBarOffset
          sideBarOffset
          className={styles['app-package-container']}
        >
          <Grid container spacing={1}>
            <Grid item xs={9}>
              <AppPackageHeader
                {...pick(application, [
                  'signedIconUrl',
                  'id',
                  'name',
                  'description',
                  'status',
                  'iconUrl',
                  'modifiedDateTime'
                ])}
                actions={application.validStateActions}
                onClickAction={this.handleApplicationAction}
              />
            </Grid>
            <Grid item xs={3} className={styles['btn-container']}>
              <MenuWrapper
                items={map(appPackageComponentTypes, componentType => ({
                  value: componentType.type,
                  label: componentType.label,
                  props: {
                    disabled:
                      (componentType.type === 'oauth2RedirectUrl' &&
                        !isEmpty(componentListByGroup.oauth2RedirectUrls)) ||
                      (componentType.type === 'appSwitcherShortcut' &&
                        !isEmpty(componentListByGroup.appSwitcherShortcuts))
                  }
                }))}
                onClickMenuItem={this.openNewComponentForm}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  disableRipple
                  className={styles['btn-addComponent']}
                  startIcon={<AddIcon />}
                >
                  Add Component
                </Button>
              </MenuWrapper>
              <Button
                variant="outlined"
                color="primary"
                className={styles['btn-edit']}
                onClick={this.handleEditApplication}
                startIcon={<Edit />}
              >
                Edit
              </Button>
            </Grid>
          </Grid>
          {this.props.error && <FailureScreen message={this.props.error} />}
          {!this.props.error &&
            !hasComponents &&
            <OverviewNullState
              src={noComponentsImg}
              imageSize="105px"
              title="No Components"
              description={
                <p className={styles['noComponentDesc']}>
                  Use the <strong>ADD COMPONENT</strong> button to get started
                </p>
              }
              descriptionLink={
                <span>
                  If you need help, take a look at the
                  <br />
                  <DocumentationLink src="https://docs.veritone.com/#/applications/quick-start/">
                    How to Add a Component to an Application
                  </DocumentationLink>
                </span>
              }
              componentProps={{
                container: {
                  sideBarOffset: false
                },
                title: {
                  className: styles['noComponentTitle']
                }
              }}
            />}
          {!this.props.error &&
            injectInto(this.props.children, {
              application: application,
              componentListByGroup: componentListByGroup,
              appPackageComponentTypes: map(appPackageComponentTypes, 'type'),
              onComponentCardClick: this.handleComponentCardClick
            })}
          {selectedComponentType &&
            <AppPackageComponentForm
              applicationId={this.props.application.id}
              title={`${this.state.isEditing
                ? 'Edit'
                : 'Add'} ${selectedComponentLabel}`}
              isEditing={this.state.isEditing}
              componentType={selectedComponentType}
              componentLabel={selectedComponentLabel}
              onSubmit={this.handleSubmitComponentData}
              onClose={this.closeNewComponentForm}
              initialValues={this.state.initialValues}
              validate={composeValidations([
                appPackageComponentModel.validate(selectedComponentType),
                requireFields(appPackageComponentModel.requiredFields)
              ])}
            />}
        </Container>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/client/plugins/applications/components/ApplicationDetailsContainer.js